import { useState } from "react";
import { X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function PlayerEmailChangeDialog({ player, onClose, onChanged }) {
  const { t } = useI18n();
  const active = player.status === "active";
  const [email, setEmail] = useState(player.email || "");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.patch(`/care/players/${player.id}/email`, {
        email, reason: reason.trim() || null, confirmed: active ? confirmed : false,
      });
      await onChanged?.(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && String(detail).includes("already exists")) setError(t("emailAlreadyInUse"));
      else if (detail?.code === "email_change_confirmation_required") setError(t("emailChangeConfirmationError"));
      else if (err.response?.status === 422 && Array.isArray(detail)) setError(t("invalidEmailAddress"));
      else setError(apiError(detail?.message || detail));
    } finally { setBusy(false); }
  };

  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.72)", backdropFilter: "blur(4px)" }} onClick={onClose}>
    <div className="pc-card p-6 w-full pc-fade" style={{ maxWidth: 480 }} onClick={(event) => event.stopPropagation()} data-testid="player-email-change-dialog">
      <div className="flex items-start justify-between gap-3"><div><h3 className="text-lg font-display font-semibold">{t("changePlayerEmail")}</h3><p className="text-sm mt-1" style={{ color: "var(--pc-muted)" }}>{player.name}</p></div><button type="button" className="pc-btn pc-btn-ghost" style={{ padding: 8 }} onClick={onClose}><X size={16} /></button></div>
      <form className="space-y-4 mt-5" onSubmit={submit}>
        <label className="block text-xs" style={{ color: "var(--pc-muted)" }}>{t("newLoginEmail")}<input data-testid="player-email-input" className="pc-input mt-1" type="email" autoComplete="off" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label className="block text-xs" style={{ color: "var(--pc-muted)" }}>{active ? t("emailChangeReasonRequired") : t("emailChangeReasonOptional")}<textarea data-testid="player-email-reason" className="pc-input mt-1" rows="3" required={active} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        {active ? <label className="flex items-start gap-2 text-sm"><input data-testid="player-email-confirm" type="checkbox" className="mt-1" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>{t("confirmActiveEmailChange")}</span></label> : <p className="text-xs rounded-xl p-3" style={{ color: "var(--pc-muted)", background: "var(--pc-card-inset)" }}>{t("invitedEmailChangeHint")}</p>}
        {error && <p role="alert" data-testid="player-email-error" className="text-sm" style={{ color: "var(--pc-score-poor)" }}>{error}</p>}
        <div className="flex justify-end gap-2"><button type="button" className="pc-btn pc-btn-ghost" onClick={onClose}>{t("cancel")}</button><button data-testid="player-email-submit" className="pc-btn pc-btn-primary" disabled={busy || active && (!confirmed || !reason.trim())}>{busy ? "…" : t("saveEmailAndContinue")}</button></div>
      </form>
    </div>
  </div>;
}
